<template>
  <div class="row">
    <div class="col-xs-12 col-md-9 col-lg-8">
      <div class="primary-action-button-group">
        <CopyButton
          v-if="!developPLWPermissions.block"
          @copied="statementCopied"
          btn-class="btn btn-default default-action-button"
          :copy-text="generatedPresentLevels"
          success-text="Present levels statement copied"
          error-text="Error copying text. Use Ctrl+C instead.">Copy Statement</CopyButton>
        <button
          v-else
          class="btn btn-default default-action-button"
          @click="blockCopyButton">Copy Statement</button>
        <button
          type="button"
          class="btn btn-primary primary-action-button"
          @click="editStatements">Edit Statements</button>
      </div>
      <div class="mdc-card">
        <section class="mdc-card__supporting-text gb-text-2">
          <WikiText
            class="gb-text-3 pl-editable-textarea fs-block"
            :locked="blurPLWPermissions.blur"
            :text="generatedPresentLevels"/>
        </section>
      </div>
    </div>
    <div class="col-xs-12 col-md-3 col-lg-4">
      <save-to-library-panel
        :content-uuid="uuid"
        :content-type="contentType"
        :content-last-save="lastUpdate && lastUpdate.toString()"
        :content-saving="saving"
        :collection-uuid="savedPLCollectionUuid"
        :collection-name="savedPLCollectionName"
        :content="{demo: false}"
        @saved:success="handleSaved"
        @removed:success="handleRemoved" />
    </div>
  </div>
</template>

<script>
import { populatePlaceholderText } from '@/api/presentlevels'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import WikiText from '@/components/WikiText'
import CopyTrackWrapper from '@/components/CopyTrackWrapper'
import CopyButton from '@/components/CopyButton'
import SaveToLibraryPanel from '@/components/presentlevels/SaveToLibraryPanel'
import PresentLevelsConclusionTabs from '@/components/presentlevels/PresentLevelsConclusionTabs'
import { mapMutations, mapActions, mapGetters, mapState } from 'vuex'
import { authorizationCheckMixin } from '@/router/authorization_mixin'
import { blockPlwMixin } from '@/components/presentlevels/block_plw_mixin'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { SEL_DEFAULT_AREA_PLW, SEL_REPLACEMENT_STRATEGIES_PLW } from '@/contentprofile/no_sel_mixins'

const RESELECT_MODES = Object.freeze({
  BACK_MODE: Symbol('Previous route was the present statement selection view.'),
  REPLACE_MODE: Symbol('Direct navigation or through library to present level conclusion view.')
})

export default {
  name: 'PresentlevelsConclusionStatement',
  components: {
    WikiText,
    CopyTrackWrapper,
    CopyButton,
    SaveToLibraryPanel,
    PresentLevelsConclusionTabs
  },
  mixins: [authorizationCheckMixin, blockPlwMixin],
  props: {
    uuid: {
      required: true,
      type: String
    },
    presentLevelStatement: {
      type: String,
      default: ''
    },
    studentName: {
      type: String,
      default: ''
    },
    studentUuid: {
      type: String,
      default: ''
    },
    enrolledGrade: {
      type: String,
      default: ''
    },
    selectedSubjects: {
      type: Array,
      default: ()=> []
    }
  },
  data() {
    return {
      reselectMode: RESELECT_MODES.REPLACE_MODE,
      contentType: CONTENT_TYPES.PRESENTLEVEL
    }
  },
  computed: {
    ...mapState('presentlevels', {
      savedPLCollectionUuid: state => state.libraryState.collectionUuid,
      savedPLCollectionName: state => state.libraryState.collectionName,
      saving: state => state.saving,
      lastUpdate: state => state.lastUpdate
    }),
    ...mapGetters('visibility',['selVisibility']),
    generatedPresentLevels: function() {
      let presentLevelsDisplay = wikiToPlainText(this.presentLevelStatement)
      if (!this.selVisibility) {
        presentLevelsDisplay = presentLevelsDisplay.replaceAll(SEL_DEFAULT_AREA_PLW, SEL_REPLACEMENT_STRATEGIES_PLW)
        presentLevelsDisplay = presentLevelsDisplay.replaceAll(SEL_DEFAULT_AREA_PLW.toUpperCase(), SEL_REPLACEMENT_STRATEGIES_PLW.toUpperCase())
      }

      // Replace placeholder text
      if (this.studentName) {
        presentLevelsDisplay = populatePlaceholderText(presentLevelsDisplay, {
          studentName: this.studentName,
          studentGrade: this.enrolledGrade
        })
      } else {
        presentLevelsDisplay = 'Generating statement...'
      }
      return presentLevelsDisplay
    }
  },
  methods: {
    ...mapMutations('presentlevels', [
      'setLibraryState',
    ]),
    ...mapActions('presentlevels', ['generateGoalShortcutData', 'generateStrategyShortcutData']),
    editStatements() {
      this.$sendEvent('Clicked Edit Statements from Drafted')
      if (this.shouldBlockPLW()) {
        return
      }
      if (this.reselectMode === RESELECT_MODES.BACK_MODE) {
        this.$router.go(-1)
      } else if (this.reselectMode === RESELECT_MODES.REPLACE_MODE) {
        this.$router.replace({ name: 'PresentlevelsResults', params: { uuid: this.uuid } })
      }
    },
    newPresentLevels() {
      this.$sendEvent('Clicked New Present Levels from Drafted')
      if (this.shouldBlockPLW()) {
        return
      }
      this.$router.push({ name: 'PresentLevelsNewWizard' })
    },
    stopEditing(text) {
      this.generatedPresentLevels = text
    },
    statementCopied() {
      this.$sendEvent('Clicked Copy Statement from Drafted')
    },
    blockCopyButton() {
      this.statementCopied()
      this.shouldBlockPLW()
    },
    handleSaved(saveResult) {
      this.setLibraryState({ uuid: saveResult.collection.uuid, name: saveResult.collection.name })
    },
    handleRemoved(deleteResult) {
      this.setLibraryState({ uuid: '', name: '' })
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // If you came from the Present Levels Statement Selection View, then just go back
      // Otherwise, replace the current frame in the history with the Statement Selection View
      if (from.name === 'PresentlevelsResults') {
        vm.reselectMode = RESELECT_MODES.BACK_MODE
      } else {
        vm.reselectMode = RESELECT_MODES.REPLACE_MODE
      }
    })
  }
}
</script>

<style scoped lang="scss">
@import '@material/card/mdc-card';

.primary-action-button-group {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-top: 10px;
  .default-action-button,
  .primary-action-button {
    display: inline-block;
    text-decoration: none;
  }
  .btn-light {
    background-color: white;
    border-color: lightgray;
  }
  button{
    margin-left: 4px;
  }
}

h2 {
  line-height: 36px;
  text-align: center;
  margin-bottom: 20px;

  .student-name {
    font-style: italic;
    color: #3c9ad5;
  }
}

.mdc-card {
  margin-top: 15px;
  padding: 15px;
  background: transparent;
  box-shadow: none;
  border: 1px solid lightgray;
}

textarea {
  background: #ffc;
  padding: 15px;
  color: #000;
}

.pl-editable-textarea {
  background: white;
  padding: 0.5em !important;
}
</style>
